import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MessageService } from '@sla-ng/core';
import { FieldType, IField, ISelectData } from '@sla-ng/ui-material';
import { BehaviorSubject, firstValueFrom, Observable, of } from 'rxjs';
import { ICostTypeGroupDetail } from '../../../models/cost-type-group-detail.model';
import { ICostTypeGroup } from '../../../models/cost-type-group.model';
import { ICostType } from '../../../models/cost-type.model';
import { IReference } from '../../../models/reference.model';
import { CostTypeGroupResource } from '../../../resources/cost-type-group.resource';
import { CostTypeResource } from '../../../resources/cost-type.resource';
import { ReferenceResource } from '../../../resources/reference.resource';
import { ICostTypeState } from './cost-type-state.model';

let _state: ICostTypeState = {
    costTypeGroupColumns: [ 'name', 'edit-button' ],
    costTypeColumns: [ 'name', 'type', 'ledgerAccountNr', 'edit-button' ],
    costTypeGroups: [],
    costTypeGroupDialogFields: [
        { field: 'name', label: 'app.model.cost-type-group.name.label', type: FieldType.text, required: true },
    ],
    costTypeDialogFields: [],
    loading: false,
};

@Injectable({
    providedIn: 'root',
})
export class CostTypeFacade {

    protected store$: BehaviorSubject<ICostTypeState> = new BehaviorSubject<ICostTypeState>(_state);

    constructor(private myCostTypeResource: CostTypeResource,
                private myCostTypeGroupResource: CostTypeGroupResource,
                private myReferenceResource: ReferenceResource,
                private myMessageService: MessageService) {
    }

    public async fetchData(): Promise<void> {
        this.setIsloading(true);

        // Load data concurrently
        const [ orphanedCostTypes, costTypeGroups, references ]: [ ICostType[], ICostTypeGroupDetail[], IReference[] ] = await Promise.all([
            firstValueFrom(this.myCostTypeResource.getCostTypes(true, false, false)).catch(() => []),
            firstValueFrom(this.myCostTypeGroupResource.getAllCostTypeGroups()).catch(() => []),
            firstValueFrom(this.myReferenceResource.getAllReferences()).catch(() => []),
        ]);

        this.createCostTypeDialogFields(costTypeGroups, references);
        if (orphanedCostTypes.length > 0) {
            costTypeGroups.unshift({
                id: null,
                name: null,
                costTypes: orphanedCostTypes,
            });
        }
        this.updateState({ ..._state, costTypeGroups });
        this.setIsloading(false);
    }

    public async addCostTypeGroup(group: ICostTypeGroup): Promise<void> {
        this.setIsloading(true);
        const createdGroup: ICostTypeGroupDetail = await this.myCostTypeGroupResource.createCostTypeGroup(group).toPromise().catch(() => null);
        if (createdGroup !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-type-group.actions.added.label');
        }
        this.setIsloading(false);
    }

    public async editCostTypeGroup(group: ICostTypeGroup): Promise<void> {
        this.setIsloading(true);
        const editedGroup: ICostTypeGroupDetail = await this.myCostTypeGroupResource.updateCostTypeGroup(group).toPromise().catch(() => null);
        if (editedGroup !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-type-group.actions.edited.label');
        }
        this.setIsloading(false);
    }

    public async addKostenart(costType: ICostType): Promise<ICostType> {
        const createdCostType: ICostType = await this.myCostTypeResource.createCostType(costType).toPromise().catch(() => null);
        if (createdCostType !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-type.actions.added.label');
        }
        return createdCostType;
    }

    public async assignCostTypeToGroup(groupId: number, costTypeId: number): Promise<void> {
        this.setIsloading(true);
        await this.myCostTypeGroupResource.assignCostTypeToGroup(groupId, costTypeId).toPromise();
        this.setIsloading(false);
    }

    public async editKostenart(costType: ICostType): Promise<ICostType> {
        const editedCostType: ICostType = await this.myCostTypeResource.updateCostType(costType).toPromise().catch(() => null);
        if (editedCostType !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-type.actions.edited.label');
        }
        return editedCostType;
    }

    public subState(): Observable<ICostTypeState> {
        return this.store$.asObservable();
    }

    public clearState(): void {
        this.updateState({ ..._state, costTypeGroups: [] });
    }

    public async deleteCostTypeGroup(groupId: number): Promise<void> {
        this.setIsloading(true);
        try {
            await firstValueFrom(this.myCostTypeGroupResource.deleteCostTypeGroup(groupId));
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-type-group.actions.deleted.label');
            await this.fetchData();
        } catch (e) {
            console.error(e);
        }
        this.setIsloading(false);
    }

    public async deleteCostType(costTypeId: number): Promise<void> {
        this.setIsloading(true);
        try {
            await firstValueFrom(this.myCostTypeResource.deleteCostType(costTypeId));
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.cost-type.actions.deleted.label');
            await this.fetchData();
        } catch (e) {
            console.error(e);
        }
        this.setIsloading(false);
    }

    private updateState(state: ICostTypeState): void {
        this.store$.next(_state = state);
    }

    private setIsloading(loading: boolean): void {
        this.updateState({ ..._state, loading });
    }

    private createCostTypeDialogFields(costTypeGroups: ICostTypeGroupDetail[], references: IReference[]): void {

        const costTypeGroupSelectData: ISelectData<ICostTypeGroup> = {
            displaySearch: true,
            labelKeys: [ 'name' ],
            valueKey: 'id',
            options: of(costTypeGroups),
        };

        const distributionReferenceSelectData: ISelectData<IReference> = {
            displaySearch: true,
            labelKeys: [ 'name' ],
            valueKey: 'id',
            options: of(references),
        };

        const costTypeDialogFields: IField[] = [
            { field: 'name', label: 'app.model.cost-type.name.label', type: FieldType.text, required: true },
            {
                field: 'ledgerAccountNr',
                label: 'app.model.cost-type.ledgerAccountNr.label',
                type: FieldType.text,
                required: false,
            },
            {
                field: 'isPrimary',
                label: 'app.model.cost-type.isPrimary.label',
                type: FieldType.checkbox,
                required: true,
                validatorFn: [ this.validateSecondaryCostType() ],
            },
            {
                field: 'costTypeGroupId',
                label: 'app.model.cost-type-group.label',
                type: FieldType.select,
                selectData: costTypeGroupSelectData,
                required: true,
            },
            {
                field: 'distributionReferenceId',
                label: 'app.model.cost-type.distribution-reference.label',
                type: FieldType.select,
                selectData: distributionReferenceSelectData,
                required: false,
            },
        ];

        this.updateState({ ..._state, costTypeDialogFields });

    }


    private validateSecondaryCostType(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors => {
            if (!control.value) {
                control.parent.get('distributionReferenceId').patchValue(null, { emitEvent: false });
                control.parent.get('ledgerAccountNr').patchValue(null, { emitEvent: false });
                !control.parent.get('ledgerAccountNr').disabled ? control.parent.get('ledgerAccountNr').disable({ emitEvent: false }) : null;
                !control.parent.get('distributionReferenceId').disabled ? control.parent.get('distributionReferenceId').disable({ emitEvent: false }) : null;
            } else {
                !control.parent.get('ledgerAccountNr').enabled ? control.parent.get('ledgerAccountNr').enable({ emitEvent: false }) : null;
                !control.parent.get('distributionReferenceId').enabled ? control.parent.get('distributionReferenceId').enable({ emitEvent: false }) : null;
            }
            return null;
        };
    }
}
